import React, { useMemo } from 'react';
import { View } from 'react-native';

import { useProviderAgenda } from '@/hooks/use-appointments';
import { APPOINTMENT_STATUS } from '@/types/appointment';
import { formatAppointmentWhen, zonedDateKey, zonedLocalToIso } from '@/utils/format-appointment';
import { SummaryEmpty, SummaryRow, SummarySkeleton, SummaryStat } from '../home-summary-dialog';

const ACTIVE_STATUS = [APPOINTMENT_STATUS.PENDING, APPOINTMENT_STATUS.CONFIRMED];

function addDaysKey(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Prévia da Agenda: próximos agendamentos dos próximos 7 dias. */
export function AgendaSummary() {
  const todayKey = useMemo(() => zonedDateKey(new Date().toISOString()), []);
  const from = useMemo(() => zonedLocalToIso(todayKey, '00:00'), [todayKey]);
  const to = useMemo(() => zonedLocalToIso(addDaysKey(todayKey, 7), '00:00'), [todayKey]);

  const { data, isLoading } = useProviderAgenda(from, to);

  const active = (data ?? [])
    .filter((appointment) => ACTIVE_STATUS.includes(appointment.status))
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const todayCount = active.filter(
    (appointment) => zonedDateKey(appointment.startAt) === todayKey,
  ).length;
  const pendingCount = active.filter(
    (appointment) => appointment.status === APPOINTMENT_STATUS.PENDING,
  ).length;

  return (
    <View className="gap-3">
      <View className="flex-row gap-2">
        <SummaryStat label="Hoje" value={`${todayCount}`} />
        <SummaryStat label="Aguardando aprovação" value={`${pendingCount}`} />
      </View>

      {isLoading ? (
        <SummarySkeleton rows={3} />
      ) : active.length ? (
        <View className="gap-2">
          {active.slice(0, 4).map((appointment) => (
            <SummaryRow
              key={appointment.id}
              icon="calendar-outline"
              title={appointment.clientName || 'Cliente'}
              description={`${appointment.serviceName ?? 'Atendimento'} · ${formatAppointmentWhen(appointment.startAt)}`}
              value={
                appointment.status === APPOINTMENT_STATUS.PENDING ? 'Pendente' : 'Confirmado'
              }
              valueTone={
                appointment.status === APPOINTMENT_STATUS.PENDING ? 'negative' : 'positive'
              }
            />
          ))}
        </View>
      ) : (
        <SummaryEmpty message="Nenhum agendamento nos próximos 7 dias." />
      )}
    </View>
  );
}
